// PortfolioTable.tsx
'use client';

import React, { useCallback, useMemo, useState } from 'react';
import { useKernelStore } from '@/kernel/kernelStores';
import { EntityView } from '@/components/entities/EntityView';
import { ViewModeGraphicToggle, type InventoryVisualMode } from '@/components/portal/ViewModeGraphicToggle';
import { supportsInventoryViewModeToggle } from '@/components/portal/inventoryViewModeCapabilities';
import { Building2, Sparkles, CheckCircle, MapPin, Hash, Activity, Briefcase, Clock, Landmark, Layers, Tag, Target } from '@/components/ui/lucide-memo';
import { LeasingPortfolioDTO, LeasingPortfolioFilters, PortfolioSortField } from '#types/types';
import { Table, TableColumnDef } from '../../../portal/Table';

const translations = {
  en: {
    portfolioHeader: 'Portfolio',
    typeHeader: 'Type',
    categoryHeader: 'Category',
    locationHeader: 'Location',
    intentHeader: 'Intent',
    propertiesHeader: 'Assets in DB',
    statusHeader: 'Status',
    updatedHeader: 'Updated',
    sortName: 'Name',
    sortIntent: 'Intent Score',
    sortAssets: 'Assets In DB',
    sortStatus: 'Status',
    sortUpdated: 'Last Updated',
    sortCreated: 'Created Date',
  },
  fr: {
    portfolioHeader: 'Portfolio',
    typeHeader: 'Type',
    categoryHeader: 'Categorie',
    locationHeader: 'Localisation',
    intentHeader: 'Intent',
    propertiesHeader: 'Actifs en base',
    statusHeader: 'Statut',
    updatedHeader: 'Mise a jour',
    sortName: 'Nom',
    sortIntent: "Score d'intent",
    sortAssets: 'Actifs en base',
    sortStatus: 'Statut',
    sortUpdated: 'Derniere maj',
    sortCreated: 'Date de creation',
  },
};

interface PortfoliosTableProps {
  lockedFilters?: Partial<LeasingPortfolioFilters>;
  assetLedgerTitle?: string;
  assetLedgerBreadcrumb?: string;
  viewModeVisual?: InventoryVisualMode;
  onViewModeVisualChange?: (mode: InventoryVisualMode) => void;
  preview?: boolean;
}

export const PortfoliosTable = React.memo(({
  lockedFilters,
  assetLedgerTitle = 'All portfolios',
  assetLedgerBreadcrumb = 'Inventory / Portfolios',
  viewModeVisual,
  onViewModeVisualChange,
  preview = false,
}: PortfoliosTableProps) => {
  const lang = useKernelStore((state) => state.language);
  const t = translations[lang];
  const [localViewModeVisual, setLocalViewModeVisual] = useState<InventoryVisualMode>('table');
  const resolvedViewModeVisual = viewModeVisual ?? localViewModeVisual;
  const handleViewModeVisualChange = useCallback((mode: InventoryVisualMode) => {
    onViewModeVisualChange?.(mode);
    if (!onViewModeVisualChange) {
      setLocalViewModeVisual(mode);
    }
  }, [onViewModeVisualChange]);
  const initialFilters = useMemo<Partial<LeasingPortfolioFilters>>(
    () => ({ sortBy: 'ASSETS', sortDir: 'desc', minAssets: 1 }),
    []
  );
  const filterOptions = useMemo(
    () => [
      { key: 'type', label: t.typeHeader, icon: <Building2 size={16} strokeWidth={1.9} /> },
      { key: 'category', label: t.categoryHeader, icon: <Sparkles size={16} strokeWidth={1.9} /> },
      { key: 'status', label: t.statusHeader, icon: <CheckCircle size={16} strokeWidth={1.9} /> },
      { key: 'country', label: t.locationHeader, icon: <MapPin size={16} strokeWidth={1.9} /> },
      {
        key: 'minAssets',
        label: lang === 'fr' ? 'Min actifs' : 'Min assets',
        icon: <Hash size={16} strokeWidth={1.9} />,
        localAccessor: (item: LeasingPortfolioDTO) => item.realPropertyCount ?? item.propertyCount ?? 0,
      },
    ],
    [lang, t]
  );
  const columns = useMemo<TableColumnDef[]>(() => [
    { id: 'portfolio', icon: Briefcase, minWidth: 200, header: t.portfolioHeader },
    { id: 'type', icon: Tag, minWidth: 100, header: t.typeHeader },
    { id: 'category', icon: Layers, minWidth: 100, header: t.categoryHeader },
    { id: 'location', icon: MapPin, minWidth: 140, header: t.locationHeader },
    { id: 'intent', icon: Target, minWidth: 80, header: t.intentHeader },
    { id: 'properties', icon: Landmark, minWidth: 80, header: t.propertiesHeader, align: 'right' },
    { id: 'status', icon: Activity, minWidth: 100, header: t.statusHeader },
    { id: 'updated', icon: Clock, minWidth: 100, header: t.updatedHeader, align: 'right' },
    { id: 'actions', minWidth: 100, header: '', align: 'right' },
  ], [t]);

  const sortOptions = useMemo<{ label: string; field: PortfolioSortField }[]>(() => [
    { label: t.sortName, field: 'NAME' },
    { label: t.sortIntent, field: 'INTENT' },
    { label: t.sortAssets, field: 'ASSETS' },
    { label: t.sortStatus, field: 'STATUS' },
    { label: t.sortUpdated, field: 'UPDATED' },
    { label: t.sortCreated, field: 'CREATED' },
  ], [t]);
  const canShowViewModeToggle = supportsInventoryViewModeToggle('Portfolio');

  const renderToolbarExtras = useCallback(
    () =>
      canShowViewModeToggle ? (
        <ViewModeGraphicToggle
          mode={resolvedViewModeVisual}
          onModeChange={handleViewModeVisualChange}
        />
      ) : null,
    [canShowViewModeToggle, resolvedViewModeVisual, handleViewModeVisualChange]
  );

  const renderRow = useCallback(
    (
      portfolio: LeasingPortfolioDTO,
      isExpanded: boolean,
      toggle: () => void,
      currentColumns?: TableColumnDef[]
    ) => (
      <div className="flex w-full flex-none flex-col">
        <EntityView
          key={portfolio.id}
          type="Portfolio"
          entityId={portfolio.id}
          variant="row"
          onClick={toggle}
          columns={currentColumns}
          className={isExpanded ? 'bg-white/[0.02] border-white/10' : ''}
        />
        {isExpanded && (
          <EntityView
            type="Portfolio"
            entityId={portfolio.id}
            variant="page"
            pageHasEntityRowAbove
            className="h-auto border-b border-[#2A2A2A]"
          />
        )}
      </div>
    ),
    []
  );

  return (
    <Table
      entityType="Portfolio"
      preview={preview}
      initialFilters={initialFilters}
      lockedFilters={lockedFilters}
      filterOptions={filterOptions}
      columns={columns}
      sortOptions={sortOptions}
      assetLedgerTitle={assetLedgerTitle}
      assetLedgerBreadcrumb={assetLedgerBreadcrumb}
      renderToolbarExtras={renderToolbarExtras}
      renderRow={renderRow}
    />
  );
});

PortfoliosTable.displayName = 'PortfoliosTable';


